import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { assetsTableStyles } from '../../utils/tableStyles';
import TableCard from '../../components/UI/TableToolbar/TableCard';
import TableToolbar from '../../components/UI/TableToolbar/TableToolbar';
import { TableSkeleton } from '../../components/UI/Skeleton/Skeleton';
import RowActionMenu from '../../components/UI/RowActionMenu/RowActionMenu';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import _debounce from 'lodash/debounce';
import { useForm } from 'react-hook-form';
import DataTable from 'react-data-table-component';
import { yupResolver } from '@hookform/resolvers/yup';

import { useGetEmployeeQuery, useGetEmployeeDropDownQuery } from '../../redux/apis/employee';
import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import { useGetDepartmentQuery } from '../../redux/apis/department';
import { selectApiLocality } from '../../redux/utils/apiLocality';

import EmptyData from '../../components/UI/EmptyData';
import PageHeader from '../../components/common/PageHeader';
import AssetsCard from '../../components/Employees/AssetsCard';
// import DeleteModel from '../../components/common/DeleteModel';

import { LB_ASSETS_TABLE_COLS, LB_HEADER } from '../../utils/constant/labels';
import { ADMIN_HR_ROLES, GLOBAL } from '../../utils/constant/global';
import { fullName } from '../../utils/handler';
import { notification } from '../../utils/notification';
import { paginationRow, skipRow } from '../../utils/handler';
import { buildAssetsSchema } from '../../utils/schemas/auth';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';
import { BT_BUTTON_LABELS } from '../../utils/constant/buttons';
import { INPUT_LABEL, PLACEHOLDER } from '../../utils/constant/placeHolders';
import {
  useAddAssetsMutation,
  //useDeleteAssetsMutation,
  useEditAssetsMutation,
  useGetAssetsQuery
} from '../../redux/apis/AssetsApi';
import {
  useAddAssetFieldMutation,
  useDeleteAssetFieldMutation,
  useEditAssetFieldMutation,
  useGetAssetFieldsQuery
} from '../../redux/apis/AssetFieldsApi';
import {
  DEFAULT_ASSET_FIELD_KEYS,
  mergeAssetFields
} from '../../utils/constant/defaultAssetFields';
import './EmployeeAssets.scss';

const RESERVED_ASSET_FIELD_KEYS = new Set([
  'user',
  'userId',
  ...DEFAULT_ASSET_FIELD_KEYS,
  'mobileDevice',
  '_id',
  'createdAt',
  'updatedAt',
  '__v'
]);

function loadCustomAssetFields() {
  return mergeAssetFields([]);
}

function displayLabelToCamelCase(label) {
  const parts = label
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^a-zA-Z0-9]/g, ''))
    .filter((w) => w.length > 0);
  if (!parts.length) return '';
  const [first, ...rest] = parts;
  return (
    first.charAt(0).toLowerCase() +
    first.slice(1).toLowerCase() +
    rest.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('')
  );
}

function humanizeLabel(labelOrKey = '') {
  const s = String(labelOrKey || '').trim();
  if (!s) return '';
  const spaced = s
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Table headers only: title-case each word so ALL CAPS reads cleanly without breaking mixed-case labels. */
function formatTableHeaderLabel(labelOrKey = '') {
  const spaced = humanizeLabel(labelOrKey);
  if (!spaced) return '';
  return spaced
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

const ASSETS_EMPLOYEE_COL_WIDTH = '180px';
const ASSETS_ACTION_COL_WIDTH = '92px';
const ASSETS_FIELD_COL_MIN = 116;
const ASSETS_FIELD_COL_MAX = 172;

const assetsColumnWidthStyle = (width) => ({
  width,
  minWidth: width,
  maxWidth: width,
  flexGrow: 0,
  flexShrink: 0
});

const getAssetFieldColWidth = (label) => {
  const text = formatTableHeaderLabel(label || '');
  const px = Math.min(ASSETS_FIELD_COL_MAX, Math.max(ASSETS_FIELD_COL_MIN, text.length * 8 + 28));
  return `${px}px`;
};

function AssetFieldCell({ value }) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return <span className="assets-table__badge assets-table__badge--empty">—</span>;
  }
  const lower = raw.toLowerCase();
  if (lower === 'yes') {
    return <span className="assets-table__badge assets-table__badge--yes">Yes</span>;
  }
  if (lower === 'no') {
    return <span className="assets-table__badge assets-table__badge--no">No</span>;
  }
  return (
    <span className="assets-table__field-value" title={raw}>
      {raw}
    </span>
  );
}

function AssetColumnHeader({ label, nowrap = false }) {
  const text = formatTableHeaderLabel(label);
  return (
    <span
      className={`assets-table__col-head${nowrap ? ' assets-table__col-head--nowrap' : ''}`}
      title={text}
    >
      {text}
    </span>
  );
}

function safeFullName(userLike) {
  if (!userLike) return '';
  try {
    return fullName(userLike) || '';
  } catch (e) {
    return userLike?.name || '';
  }
}

const initialState = {
  isModal: false,
  isEditModalData: {},
  search: '',
  department: '',
  modalHeader: { title: '', isEdit: false },
  isEditModal: false,
  isDeleteModal: false,
  isDelete: { id: '', status: '' },
  currentPage: 1,
  rowsPerPage: 10,
  isNext: false,
  sortField: 'user.name',
  sortDirection: false,
  loading: false,
  selectedUserId: '',
  empData: [],
  dropdownDataStore: [],
  currentPageDropdown: 1,
  rowsPerPageDropdown: 50,
  dropdownSearch: ''
};

const { LB_TBL_COL_EMPLOYEE_NAME, LB_TBL_COL_ACTION } = LB_ASSETS_TABLE_COLS;
const {
  LB_ADD_ASSETS,
  LB_ADD_ASSETS_FIELD,
  LB_APPROVE,
  LB_SUCCESS_MSG,
  LB_EMPLOYEE_ASSETS,
  LB_ASSETS
} = LB_HEADER;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;
const { DESC, ASC } = GLOBAL;
const { PH_ASSETS_FIELD_NAME } = PLACEHOLDER;
const {
  BTN_SAVE,
  BTN_ACCEPT,
  // BTN_REJECT: REJECT_BTN,
  BTN_ADD_ASSETS,
  BTN_ADD_ASSETS_FIELD,
  BTN_CANCEL
} = BT_BUTTON_LABELS;
const { LB_USER, LB_ASSETS_FIELD_NAME } = INPUT_LABEL;

function EmployeeAssets() {
  const [state, setState] = useState(initialState);
  const [customAssetFields, setCustomAssetFields] = useState(loadCustomAssetFields);
  const [activeAdminTab, setActiveAdminTab] = useState('assets'); // 'assets' | 'fields'
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
  const [newAssetFieldLabel, setNewAssetFieldLabel] = useState('');
  const [editingAssetFieldId, setEditingAssetFieldId] = useState(null);
  const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
  const [deleteFieldTarget, setDeleteFieldTarget] = useState(null);
  const [selectedAssetFieldKeys, setSelectedAssetFieldKeys] = useState([]);

  const { data: loginData } = useIsLoggedInQuery();
  const roleName = loginData?.data?.hrxRole?.roleName;
  const isAdminOrHr = ADMIN_HR_ROLES.includes(roleName);
  const loggedInUserId = loginData?.data?._id;
  const locality = useSelector(selectApiLocality);

  const { data: departmentData } = useGetDepartmentQuery({}, { skip: !isAdminOrHr });

  const { refetch: refetchEmployee } = useGetEmployeeQuery({
    search: '',
    skip: skipRow(state.currentPage, state.rowsPerPage),
    limit: state.rowsPerPage
  });
  const { data: dropdownData, refetch: employeeDropDownQuery } = useGetEmployeeDropDownQuery(
    {
      skip: skipRow(state.currentPageDropdown, state.rowsPerPageDropdown),
      limit: state.rowsPerPageDropdown,
      search: state.dropdownSearch
    },
    { skip: !isAdminOrHr }
  );
  const {
    data: assetsData,
    isLoading,
    refetch: refetchAssets
  } = useGetAssetsQuery({
    search: state.search,
    skip: skipRow(state.currentPage, state.rowsPerPage),
    limit: state.rowsPerPage,
    sort: state.sortField,
    sortType: state.sortDirection ? DESC : ASC,
    department: isAdminOrHr && state.department ? state.department : undefined,
    userId: isAdminOrHr ? undefined : loggedInUserId
  });

  const { data: assetFieldsData, refetch: refetchAssetFields } = useGetAssetFieldsQuery(
    {},
    { skip: !locality?.length }
  );
  const [addAssets] = useAddAssetsMutation();
  const [editAssetsApi] = useEditAssetsMutation();
  // const [deleteAssets] = useDeleteAssetsMutation();
  const [addAssetField] = useAddAssetFieldMutation();
  const [editAssetField] = useEditAssetFieldMutation();
  const [deleteAssetField] = useDeleteAssetFieldMutation();

  const handleChangeSort = (sortField) => {
    setState({ ...state, sortDirection: !state.sortDirection, sortField: sortField.sortField });
  };

  const assetFieldsList = useMemo(() => {
    if (Array.isArray(assetFieldsData?.data)) return assetFieldsData.data;
    if (Array.isArray(assetFieldsData?.data?.data)) return assetFieldsData.data.data;
    return [];
  }, [assetFieldsData]);

  useEffect(() => {
    const next = assetFieldsList
      .filter(
        (f) =>
          f && typeof f._id === 'string' && typeof f.key === 'string' && typeof f.name === 'string'
      )
      .map((f) => ({ id: f._id, key: f.key, label: f.name, isRequired: !!f.isRequired }));
    setCustomAssetFields(mergeAssetFields(next));
  }, [assetFieldsList]);

  const extraDynamicAssetFields = useMemo(
    () => customAssetFields.filter((f) => !DEFAULT_ASSET_FIELD_KEYS.has(f.key)),
    [customAssetFields]
  );

  const standardAssetFields = useMemo(
    () => customAssetFields.filter((f) => DEFAULT_ASSET_FIELD_KEYS.has(f.key)),
    [customAssetFields]
  );

  const assetFieldIdByKey = useMemo(() => {
    const m = new Map();
    customAssetFields.forEach((f) => m.set(f.key, f.id));
    return m;
  }, [customAssetFields]);

  const assetFieldKeyById = useMemo(() => {
    const m = new Map();
    customAssetFields.forEach((f) => m.set(f.id, f.key));
    return m;
  }, [customAssetFields]);

  const assetFieldOptions = useMemo(() => {
    return extraDynamicAssetFields.map((f) => ({ value: f.key, label: f.label }));
  }, [extraDynamicAssetFields]);

  const selectedAssetFieldOptions = useMemo(() => {
    const map = new Map(extraDynamicAssetFields.map((f) => [f.key, f.label]));
    return selectedAssetFieldKeys
      .map((key) => ({ value: key, label: map.get(key) || key }))
      .filter((x) => !!x.label);
  }, [extraDynamicAssetFields, selectedAssetFieldKeys]);

  useEffect(() => {
    // When opening modal (add/edit), pre-select extra dynamic fields that already have values.
    if (state.isModal) {
      if (state.isEditModalData?._id) {
        const keysWithValues = Array.isArray(state.isEditModalData?.dynamicFields)
          ? state.isEditModalData.dynamicFields
              .map((df) => {
                const fieldId = df?.fieldId;
                if (fieldId && typeof fieldId === 'object' && typeof fieldId.key === 'string') {
                  return fieldId.key;
                }
                if (typeof fieldId === 'string') return assetFieldKeyById.get(fieldId) || '';
                return '';
              })
              .filter((key) => key && !DEFAULT_ASSET_FIELD_KEYS.has(key))
          : [];
        setSelectedAssetFieldKeys(keysWithValues);
      } else {
        setSelectedAssetFieldKeys([]);
      }
    }
  }, [state.isModal, state.isEditModalData, assetFieldKeyById]);

  useEffect(() => {
    const allowed = new Set(extraDynamicAssetFields.map((f) => f.key));
    setSelectedAssetFieldKeys((prev) => prev.filter((k) => allowed.has(k)));
  }, [extraDynamicAssetFields]);

  const handleSaveCustomAssetField = async () => {
    const label = newAssetFieldLabel.trim();
    if (!label) {
      notification('Please enter an assets field name', TOAST_ERROR);
      return;
    }
    const key = displayLabelToCamelCase(label);
    if (!editingAssetFieldId) {
      if (!key) {
        notification('Invalid field name', TOAST_ERROR);
        return;
      }
      if (RESERVED_ASSET_FIELD_KEYS.has(key)) {
        notification('This name conflicts with a built-in field', TOAST_ERROR);
        return;
      }
      if (customAssetFields.some((f) => f.key === key)) {
        notification('This field already exists', TOAST_ERROR);
        return;
      }
    }

    try {
      const res = editingAssetFieldId
        ? await editAssetField({
            id: editingAssetFieldId,
            data: { name: label, type: 'string', isRequired: true }
          })
        : await addAssetField({
            name: label,
            key,
            type: 'string',
            isRequired: true
          });

      if (res?.data?.success) {
        refetchAssetFields();
        notification(
          res?.data?.statusMessage ||
            (editingAssetFieldId ? 'Assets field updated' : 'Assets field created'),
          TOAST_SUCCESS
        );
        setNewAssetFieldLabel('');
        setEditingAssetFieldId(null);
        setIsAddFieldModalOpen(false);
        return;
      }

      notification(
        res?.error?.data?.statusMessage ||
          (editingAssetFieldId ? 'Failed to update assets field' : 'Failed to create assets field'),
        TOAST_ERROR
      );
    } catch (e) {
      notification(
        e?.message ||
          (editingAssetFieldId ? 'Failed to update assets field' : 'Failed to create assets field'),
        TOAST_ERROR
      );
    }
  };

  const handleConfirmDeleteAssetField = async () => {
    if (!deleteFieldTarget?._id) return;
    try {
      const res = await deleteAssetField(deleteFieldTarget._id);
      if (res?.data?.success) {
        refetchAssetFields();
        notification(res?.data?.statusMessage || 'Assets field deleted', TOAST_SUCCESS);
      } else {
        notification(
          res?.error?.data?.statusMessage || 'Failed to delete assets field',
          TOAST_ERROR
        );
      }
    } catch (e) {
      notification(e?.message || 'Failed to delete assets field', TOAST_ERROR);
    } finally {
      setIsDeleteFieldModalOpen(false);
      setDeleteFieldTarget(null);
    }
  };

  const defaultValue = useMemo(() => {
    const base = {
      userId: ''
    };
    customAssetFields.forEach(({ key }) => {
      base[key] = '';
    });
    return base;
  }, [customAssetFields]);

  const getDynamicValueByKey = (row, key) => {
    const arr = row?.dynamicFields;
    if (!Array.isArray(arr)) return '';
    const match = arr.find((df) => {
      const fieldId = df?.fieldId;
      if (fieldId && typeof fieldId === 'object' && typeof fieldId.key === 'string') {
        return fieldId.key === key;
      }
      if (typeof fieldId === 'string') {
        return assetFieldKeyById.get(fieldId) === key;
      }
      return false;
    });
    return match?.value ?? '';
  };

  const getAssetValueFromRow = (row, key) => {
    const dynamic = getDynamicValueByKey(row, key);
    if (dynamic !== undefined && dynamic !== null && String(dynamic).trim() !== '') {
      return String(dynamic);
    }
    const legacy = row?.[key];
    if (legacy !== undefined && legacy !== null && String(legacy).trim() !== '') {
      return String(legacy);
    }
    return '';
  };

  const mergedFormValues = useMemo(() => {
    if (state.isEditModalData?._id) {
      const row = state.isEditModalData;
      const next = { ...defaultValue };
      customAssetFields.forEach(({ key }) => {
        next[key] = getAssetValueFromRow(row, key);
      });
      next.userId = row?.user?._id;
      return next;
    }
    return isAdminOrHr && defaultValue ? defaultValue : { ...defaultValue, userId: loggedInUserId };
  }, [state.isEditModalData, defaultValue, customAssetFields, isAdminOrHr, loggedInUserId]);

  const assetsResolver = useMemo(
    () => yupResolver(buildAssetsSchema(customAssetFields.map((f) => f.key))),
    [customAssetFields]
  );

  const {
    register,
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError
  } = useForm({
    resolver: assetsResolver,
    values: mergedFormValues
  });

  const selectedExtraFields = useMemo(() => {
    const selectedSet = new Set(selectedAssetFieldKeys);
    return extraDynamicAssetFields.filter((f) => selectedSet.has(f.key));
  }, [extraDynamicAssetFields, selectedAssetFieldKeys]);

  const employeeOptions = useMemo(() => {
    const list = state.dropdownDataStore?.length
      ? state.dropdownDataStore
      : dropdownData?.data || [];
    return list.map((val) => ({
      value: val._id,
      label: val.name
    }));
  }, [state.dropdownDataStore, dropdownData?.data]);

  const selectedUserId = watch('userId');

  const openAssetsModal = useCallback(
    (editData = {}) => {
      setState((prev) => ({
        ...prev,
        isModal: true,
        isEditModalData: editData,
        modalHeader: {
          title: editData._id ? LB_ASSETS : LB_ADD_ASSETS,
          isEdit: !!editData._id
        },
        currentPageDropdown: 1,
        dropdownSearch: ''
      }));
      if (isAdminOrHr) {
        employeeDropDownQuery();
      }
    },
    [isAdminOrHr, employeeDropDownQuery]
  );

  const columns = useMemo(() => {
    const employeeColStyle = {
      ...assetsColumnWidthStyle(ASSETS_EMPLOYEE_COL_WIDTH),
      justifyContent: 'flex-start'
    };
    const actionColStyle = assetsColumnWidthStyle(ASSETS_ACTION_COL_WIDTH);

    const baseCols = [
      {
        id: 'employeeName',
        name: <AssetColumnHeader label={LB_TBL_COL_EMPLOYEE_NAME} nowrap />,
        selector: (row) => safeFullName(row?.user || row?.userId),
        sortable: false,
        wrap: true,
        grow: 0,
        width: ASSETS_EMPLOYEE_COL_WIDTH,
        style: employeeColStyle,
        headerStyle: employeeColStyle,
        cell: (row) => {
          const userLike = row?.user || row?.userId;
          const name = safeFullName(userLike);
          return (
            <div className="assets-table__employee-cell" title={name || undefined}>
              <span className="assets-table__employee-name">{name || '—'}</span>
            </div>
          );
        }
      }
    ];

    const dynamicCols = customAssetFields.map(({ key, label }) => {
      const colWidth = getAssetFieldColWidth(label || key);
      const fieldColStyle = assetsColumnWidthStyle(colWidth);
      return {
        id: key,
        name: <AssetColumnHeader label={label || key} />,
        selector: (row) => getAssetValueFromRow(row, key) || '',
        sortable: false,
        wrap: true,
        grow: 0,
        width: colWidth,
        style: fieldColStyle,
        headerStyle: fieldColStyle,
        cell: (row) => <AssetFieldCell value={getAssetValueFromRow(row, key)} />
      };
    });

    const actionCol = {
      id: 'action',
      name: <AssetColumnHeader label={LB_TBL_COL_ACTION} nowrap />,
      selector: () => '',
      sortable: false,
      grow: 0,
      width: ASSETS_ACTION_COL_WIDTH,
      style: actionColStyle,
      headerStyle: actionColStyle,
      cell: (row) => (
        <div className="assets-table__action-cell">
          <RowActionMenu
            actions={[
              {
                label: 'Edit',
                icon: 'icofont-edit',
                variant: 'success',
                onClick: () => openAssetsModal(row)
              }
            ]}
          />
        </div>
      )
    };

    return isAdminOrHr ? [...baseCols, ...dynamicCols, actionCol] : [...baseCols, ...dynamicCols];
  }, [customAssetFields, isAdminOrHr, openAssetsModal]);

  const assetFieldColumns = useMemo(
    () => [
      {
        name: <AssetColumnHeader label="Field Name" />,
        selector: (row) => row?.name,
        sortable: false,
        wrap: true,
        grow: 2,
        style: { justifyContent: 'flex-start' },
        cell: (row) => (
          <div className="assets-fields-table__name" title={row?.name || undefined}>
            <strong>{row?.name || '—'}</strong>
          </div>
        )
      },
      {
        name: <AssetColumnHeader label="Key" />,
        selector: (row) => row?.key,
        sortable: false,
        wrap: true,
        grow: 1.4,
        cell: (row) => <code className="assets-fields-table__key">{row?.key || '—'}</code>
      },
      {
        name: <AssetColumnHeader label="Required" />,
        selector: (row) => row?.isRequired,
        sortable: false,
        width: '130px',
        cell: (row) => (
          <span
            className={`assets-table__badge ${
              row?.isRequired ? 'assets-table__badge--yes' : 'assets-table__badge--no'
            }`}
          >
            {row?.isRequired ? 'Yes' : 'No'}
          </span>
        )
      },
      {
        name: <AssetColumnHeader label="Action" nowrap />,
        sortable: false,
        width: '120px',
        cell: (row) => {
          const isBuiltIn = DEFAULT_ASSET_FIELD_KEYS.has(row?.key);
          return (
            <div
              className="assets-fields-table__actions"
              role="group"
              aria-label="Asset field actions"
            >
              <button
                type="button"
                className="assets-fields-table__icon-btn"
                title="Edit"
                disabled={isBuiltIn}
                onClick={() => {
                  setEditingAssetFieldId(row?._id);
                  setNewAssetFieldLabel(row?.name || '');
                  setIsAddFieldModalOpen(true);
                }}
              >
                <i className="icofont-edit" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="assets-fields-table__icon-btn assets-fields-table__icon-btn--danger"
                title={isBuiltIn ? 'Built-in field cannot be deleted' : 'Delete'}
                disabled={isBuiltIn}
                onClick={() => {
                  setDeleteFieldTarget(row);
                  setIsDeleteFieldModalOpen(true);
                }}
              >
                <i className="icofont-ui-delete" aria-hidden="true" />
              </button>
            </div>
          );
        }
      }
    ],
    []
  );

  const handleAddAssets = async (data) => {
    try {
      setState({ ...state, loading: true });

      const res = await addAssets({ ...data });
      if (res?.data?.success) {
        refetchAssets();
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        setState({ ...state, isModal: false, loading: false });
        reset();
      } else {
        setState({ ...state, isModal: false, loading: false });
        notification(res?.error?.data?.statusMessage, TOAST_ERROR);
        reset();
      }
    } catch (error) {
      notification(error?.response?.data?.statusMessage || 'Something went wrong', TOAST_ERROR);
      setState({ ...state, isModal: false, loading: false });
    }
  };

  const handleEditAssets = async (data) => {
    try {
      setState({ ...state, loading: true });

      const res = await editAssetsApi({
        data: { ...data },
        id: state.isEditModalData?._id
      });
      if (res?.data?.success) {
        refetchAssets();
        reset();
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        setState({ ...state, isModal: false, loading: false });
      } else {
        reset();
        setState({ ...state, isModal: false, loading: false });
        notification(res?.error?.data?.statusMessage, TOAST_ERROR);
      }
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const buildDynamicFieldEntry = (f, data) => {
    const fieldId = f.id || assetFieldIdByKey.get(f.key);
    const value = data?.[f.key];
    if (!fieldId) return null;
    if (value === undefined || value === null || String(value).trim() === '') return null;
    return { fieldId, value: String(value) };
  };

  const onSubmit = (data) => {
    let hasError = false;
    selectedExtraFields.forEach((f) => {
      const value = data?.[f.key];
      if (value === undefined || value === null || String(value).trim() === '') {
        hasError = true;
        setError(f.key, {
          type: 'manual',
          message: `${humanizeLabel(f.label || f.key)} is required`
        });
      }
    });

    if (hasError) {
      notification('Please fill all selected additional asset fields', TOAST_ERROR);
      return;
    }

    const fieldsToSave = [...standardAssetFields, ...selectedExtraFields];
    const dynamicFields = fieldsToSave.map((f) => buildDynamicFieldEntry(f, data)).filter(Boolean);

    if (!dynamicFields.length) {
      notification('Please enter at least one asset detail', TOAST_ERROR);
      return;
    }

    const payload = {
      userId: isAdminOrHr ? data.userId : loggedInUserId,
      dynamicFields
    };

    if (state.isEditModalData?._id) {
      handleEditAssets(payload);
    } else {
      handleAddAssets(payload);
    }
  };

  useEffect(() => {
    if (isAdminOrHr) refetchEmployee();
  }, []);

  //   const handleDeleteAssets = async (id) => {
  //     try {
  //       const res = await deleteAssets(id);
  //       if (res?.data?.success) {
  //         refetchAssets();
  //         setState({ ...state, isDelete: { id: '', status: false } });
  //         notification(res?.data?.statusMessage, TOAST_SUCCESS);
  //       } else {
  //         notification(res?.data?.statusMessage, TOAST_ERROR);
  //       }
  //     } catch (error) {
  //       notification(error.message, TOAST_ERROR);
  //     }
  //   };

  useEffect(() => {
    if (assetsData?.isNext === undefined) return;
    setState((prevState) =>
      prevState.isNext === assetsData.isNext
        ? prevState
        : { ...prevState, isNext: assetsData.isNext }
    );
  }, [assetsData?.isNext]);

  const handleChangePage = (page) => {
    setState({ ...state, currentPage: page });
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setState({ ...state, rowsPerPage: newRowsPerPage, currentPage: 1 });
  };

  const debouncedOnSearchData = _debounce((data) => {
    setState({ ...state, search: data, currentPage: 1 });
  }, 300);

  const handleMenuScrollToBottom = async () => {
    if (dropdownData?.isNext) {
      setState((prevState) => ({
        ...prevState,
        currentPageDropdown: prevState.currentPageDropdown + 1
      }));
    }
  };

  useEffect(() => {
    if (!dropdownData?.data || !Array.isArray(dropdownData.data)) return;

    setState((prevState) => ({
      ...prevState,
      dropdownDataStore:
        prevState.currentPageDropdown === 1
          ? dropdownData.data
          : [...prevState.dropdownDataStore, ...dropdownData.data]
    }));
  }, [dropdownData]);

  return (
    <div className="container-xxl">
      {isAdminOrHr && <PageHeader headerTitle={LB_EMPLOYEE_ASSETS} />}
      <div className="row clearfix g-3">
        <div className="col-sm-12">
          {isAdminOrHr ? (
            <TableCard>
              <TableToolbar
                className="app-table-toolbar--aligned assets-toolbar"
                showSearch={activeAdminTab === 'assets'}
                onSearch={debouncedOnSearchData}
                leading={
                  <ul className="nav nav-pills assets-admin-tabs">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${activeAdminTab === 'assets' ? 'active' : ''}`}
                        onClick={() => setActiveAdminTab('assets')}
                      >
                        Assets
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${activeAdminTab === 'fields' ? 'active' : ''}`}
                        onClick={() => setActiveAdminTab('fields')}
                      >
                        Asset Fields
                      </button>
                    </li>
                  </ul>
                }
                filters={
                  activeAdminTab === 'assets' ? (
                    <div className="assets-dept-filter">
                      <Select
                        isClearable
                        placeholder="Department..."
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        classNamePrefix="assets-dept-select"
                        styles={{
                          container: (base) => ({ ...base, width: '100%' }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base) => ({
                            ...base,
                            minHeight: 42,
                            height: 42,
                            borderRadius: 11,
                            borderColor: '#e9edf3',
                            backgroundColor: '#f8fafc',
                            fontSize: 13,
                            fontWeight: 600,
                            boxShadow: 'none'
                          })
                        }}
                        options={(departmentData?.data || []).map((d) => ({
                          value: d._id,
                          label: d.name
                        }))}
                        value={
                          state.department
                            ? (departmentData?.data || [])
                                .map((d) => ({ value: d._id, label: d.name }))
                                .find((o) => o.value === state.department) || null
                            : null
                        }
                        onChange={(opt) => {
                          setState((prev) => ({
                            ...prev,
                            department: opt?.value || '',
                            currentPage: 1
                          }));
                        }}
                      />
                    </div>
                  ) : null
                }
                actions={
                  activeAdminTab === 'fields' ? (
                    <button
                      type="button"
                      className="app-table-toolbar__add-btn"
                      onClick={() => {
                        setEditingAssetFieldId(null);
                        setNewAssetFieldLabel('');
                        setIsAddFieldModalOpen(true);
                      }}
                    >
                      <i className="icofont-plus-circle"></i>
                      {BTN_ADD_ASSETS_FIELD}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="app-table-toolbar__add-btn"
                      onClick={() => openAssetsModal()}
                    >
                      <i className="icofont-plus-circle"></i>
                      {BTN_ADD_ASSETS}
                    </button>
                  )
                }
              />

              {activeAdminTab === 'fields' ? (
                <div className="app-table-card__body">
                  <DataTable
                    columns={assetFieldColumns}
                    data={assetFieldsList || []}
                    pagination
                    dense
                    customStyles={assetsTableStyles}
                    selectableRows={false}
                    noDataComponent={<EmptyData />}
                    className="table myDataTable assets-table assets-fields-table table-hover align-middle mb-0 dataTable no-footer dtr-inline"
                    highlightOnHover={true}
                    striped
                  />
                </div>
              ) : (
                <div className="app-table-card__body assets-table-scroll">
                  <DataTable
                    columns={columns}
                    data={assetsData?.data || []}
                    defaultSortField="title"
                    pagination
                    paginationServer
                    dense
                    onSort={handleChangeSort}
                    sortField={state.sortField}
                    sortDirection={state.sortDirection}
                    responsive={false}
                    customStyles={assetsTableStyles}
                    paginationTotalRows={paginationRow(
                      assetsData?.isNext,
                      state.rowsPerPage,
                      state.currentPage,
                      assetsData?.data?.length
                    )}
                    paginationPerPage={state.rowsPerPage}
                    paginationRowsPerPageOptions={[5, 10, 20, 30]}
                    paginationComponentOptions={{
                      noRowsPerPage: false,
                      rangeSeparatorText: 'of',
                      selectAllRowsItem: state.isNext,
                      selectAllRowsItemText: 'All',
                      paginationButtonsDisabled: state.isNext
                    }}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    selectableRows={false}
                    noDataComponent={<EmptyData />}
                    className="table myDataTable assets-table assets-table--wide table-hover align-middle mb-0 dataTable no-footer dtr-inline"
                    highlightOnHover={true}
                    striped
                    progressPending={isLoading && !assetsData?.data}
                    progressComponent={<TableSkeleton columns={6} />}
                  />
                </div>
              )}
            </TableCard>
          ) : (
            <>
              {assetsData?.data?.length ? (
                <AssetsCard
                  assetsData={assetsData.data[0]}
                  assetFields={customAssetFields}
                  canEdit={true}
                  onEdit={() => openAssetsModal(assetsData.data[0])}
                />
              ) : (
                <TableCard>
                  <div className="app-table-card__body assets-empty-state text-center py-4">
                    <EmptyData />
                    <button
                      type="button"
                      className="btn btn-dark btn-set-task mt-1"
                      onClick={() => openAssetsModal()}
                    >
                      <i className="icofont-plus-circle me-2 fs-6"></i>
                      {BTN_ADD_ASSETS}
                    </button>
                  </div>
                </TableCard>
              )}
            </>
          )}
        </div>
      </div>
      {/* <DeleteModel
        isOpen={() => {
          setState({ ...state, isDelete: { id: '', status: false } });
        }}
        open={state?.isDelete?.status}
        title={'Delete Assets'}
        message={'Are you sure you want to delete this asset?'}
        isDelete={() => {
          handleDeleteAssets(state?.isDelete.id);
        }}
      /> */}
      <Modal
        centered
        className="assets-modal"
        dialogClassName="assets-modal__dialog"
        contentClassName="assets-modal__content"
        show={state?.isModal}
        onHide={() => {
          reset();
          setState({ ...state, isModal: false });
        }}
      >
        <Modal.Header className="assets-modal__header">
          <div className="assets-modal__header-content">
            <span className="assets-modal__header-icon" aria-hidden="true">
              <i className="icofont-laptop" />
            </span>
            <div className="assets-modal__header-copy">
              <Modal.Title className="fw-bold assets-modal__title">
                {state.modalHeader.title}
              </Modal.Title>
              <p className="assets-modal__subtitle">
                Enter the equipment assigned to this employee
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close assets-modal__close"
            aria-label="Close"
            onClick={() => {
              reset();
              setState({ ...state, isModal: false });
            }}
          />
        </Modal.Header>
        <form className="assets-modal__form" onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="assets-modal__body">
            <div className="deadline-form">
              {isAdminOrHr && (
                <div className="assets-modal__section">
                  <label className="assets-modal__label">{LB_USER}</label>
                  <Select
                    className="assets-modal__select"
                    classNamePrefix="assets-modal-select"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
                    onInputChange={(text) => {
                      if (text && text.length > 2) {
                        const hasLocalMatch = employeeOptions.some(({ label }) =>
                          label?.toLocaleLowerCase().startsWith(text.toLocaleLowerCase())
                        );
                        if (!hasLocalMatch) {
                          setState((prev) => ({
                            ...prev,
                            dropdownSearch: text,
                            currentPageDropdown: 1,
                            dropdownDataStore: []
                          }));
                        }
                      } else if (!text) {
                        setState((prev) => ({
                          ...prev,
                          dropdownSearch: '',
                          currentPageDropdown: 1
                        }));
                      }
                    }}
                    options={employeeOptions}
                    onMenuScrollToBottom={handleMenuScrollToBottom}
                    placeholder="Select option..."
                    value={
                      employeeOptions.find((opt) => opt.value === selectedUserId) ||
                      (state.isEditModalData?.user?._id
                        ? {
                            value: state.isEditModalData.user._id,
                            label: state.isEditModalData.user?.name
                          }
                        : null)
                    }
                    isDisabled={!!state.isEditModalData?._id}
                    onChange={(selectedOption) => {
                      setValue('userId', selectedOption?.value || '');
                      setState((prev) => ({
                        ...prev,
                        selectedUserId: selectedOption?.value || ''
                      }));
                      clearErrors('userId');
                    }}
                  />
                  <small className="text-danger assets-modal__error">
                    {errors.userId && errors.userId.message}
                  </small>
                </div>
              )}

              <div className="assets-modal__section">
                <label className="assets-modal__section-title">Standard Assets</label>
                <div className="assets-fields-scroll assets-modal__fields">
                  {standardAssetFields.map(({ key, label }) => (
                    <div className="assets-modal__field" key={key}>
                      <label htmlFor={key} className="assets-modal__label">
                        {humanizeLabel(label || key)}
                      </label>
                      <input
                        {...register(key)}
                        id={key}
                        className="form-control assets-modal__input"
                        placeholder={humanizeLabel(label || key)}
                      />
                      <small className="text-danger assets-modal__error">
                        {errors[key]?.message}
                      </small>
                    </div>
                  ))}
                </div>
              </div>

              {extraDynamicAssetFields.length > 0 && (
                <div className="assets-modal__section">
                  <label className="assets-modal__section-title">Additional Fields</label>
                  <div className="assets-modal__extra-select">
                    <Select
                      className="assets-modal__select"
                      classNamePrefix="assets-modal-select"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
                      isMulti
                      options={assetFieldOptions}
                      value={selectedAssetFieldOptions}
                      placeholder="Select additional fields..."
                      onChange={(selected) => {
                        const keys = Array.isArray(selected) ? selected.map((x) => x.value) : [];
                        setSelectedAssetFieldKeys(keys);
                      }}
                    />
                  </div>
                  <div className="assets-fields-scroll assets-modal__fields">
                    {selectedExtraFields.map(({ key, label }) => (
                      <div className="assets-modal__field" key={key}>
                        <label htmlFor={key} className="assets-modal__label">
                          {humanizeLabel(label || key)}
                        </label>
                        <input
                          {...register(key)}
                          id={key}
                          className="form-control assets-modal__input"
                          placeholder={humanizeLabel(label || key)}
                        />
                        <small className="text-danger assets-modal__error">
                          {errors[key]?.message}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="assets-modal__footer">
            <button type="submit" disabled={state.loading} className="btn assets-modal__submit">
              {state.loading ? (
                <div className="spinner-border spinner-border-sm" role="status"></div>
              ) : (
                <>
                  <i className="icofont-check-circled" aria-hidden="true" />
                  {BTN_SAVE}
                </>
              )}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
      <Modal
        centered
        className="assets-modal"
        dialogClassName="assets-modal__dialog assets-modal__dialog--compact"
        contentClassName="assets-modal__content"
        show={isAddFieldModalOpen}
        onHide={() => {
          setNewAssetFieldLabel('');
          setEditingAssetFieldId(null);
          setIsAddFieldModalOpen(false);
        }}
      >
        <Modal.Header className="assets-modal__header">
          <div className="assets-modal__header-content">
            <span className="assets-modal__header-icon" aria-hidden="true">
              <i className="icofont-listing-box" />
            </span>
            <div className="assets-modal__header-copy">
              <Modal.Title className="fw-bold assets-modal__title">
                {editingAssetFieldId ? 'Edit Assets Field' : LB_ADD_ASSETS_FIELD}
              </Modal.Title>
              <p className="assets-modal__subtitle">
                {editingAssetFieldId
                  ? 'Update the name of this asset field'
                  : 'Name the field you want to collect on employee assets'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close assets-modal__close"
            aria-label="Close"
            onClick={() => {
              setNewAssetFieldLabel('');
              setEditingAssetFieldId(null);
              setIsAddFieldModalOpen(false);
            }}
          />
        </Modal.Header>
        <Modal.Body className="assets-modal__body">
          <div className="assets-modal__section">
            <label className="assets-modal__label" htmlFor="new-asset-field-name">
              {LB_ASSETS_FIELD_NAME}
            </label>
            <input
              id="new-asset-field-name"
              className="form-control assets-modal__input"
              value={newAssetFieldLabel}
              onChange={(e) => setNewAssetFieldLabel(e.target.value)}
              placeholder={PH_ASSETS_FIELD_NAME}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveCustomAssetField();
                }
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="assets-modal__footer">
          <button
            type="button"
            className="btn assets-modal__cancel"
            onClick={() => {
              setNewAssetFieldLabel('');
              setEditingAssetFieldId(null);
              setIsAddFieldModalOpen(false);
            }}
          >
            {BTN_CANCEL}
          </button>
          <button
            type="button"
            className="btn assets-modal__submit"
            onClick={handleSaveCustomAssetField}
          >
            <i className="icofont-check-circled" aria-hidden="true" />
            {BTN_SAVE}
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        centered
        show={isDeleteFieldModalOpen}
        onHide={() => {
          setIsDeleteFieldModalOpen(false);
          setDeleteFieldTarget(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Delete Assets Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to delete <b>{deleteFieldTarget?.name || 'this field'}</b>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setIsDeleteFieldModalOpen(false);
              setDeleteFieldTarget(null);
            }}
          >
            {BTN_CANCEL}
          </button>
          <button type="button" className="btn btn-danger" onClick={handleConfirmDeleteAssetField}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        centered
        show={state?.isEditModal}
        onHide={() => {
          setState({ ...state, isEditModal: false });
        }}
      >
        <Modal.Header closeButton>
          <h5 className="modal-title  fw-bold" id="dremovetaskLabel">
            {LB_APPROVE}
          </h5>
        </Modal.Header>
        <Modal.Body className="justify-content-center flex-column d-flex">
          <i className="icofont-simple-smile text-success display-2 text-center mt-2"></i>
          <p className="mt-4 fs-5 text-center">{LB_SUCCESS_MSG}</p>
          <button className="btn btn-success">{BTN_ACCEPT}</button>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default EmployeeAssets;
